import axios from 'axios';
import { 
  FornecedoresStats, 
  PlanoAcaoStats, 
  MaturidadeData, 
  Fornecedor,
  TimelineData,
  FornecedoresParams,
  FornecedoresPaginatedResponse,
  TentativasEnvioResponse,
  TentativaEnvio,
  TentativasEnvioStats
} from '../../types/dashboard/fornecedores.types';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4002';
const API_URL = `${API_BASE}/api`;
const BASE_URL = `${API_URL}/dashboard/governanca/fornecedores`;
const INICIO_ACAO_URL = `${BASE_URL}/inicio-acao`;
const PRIMEIRO_CADASTRO_URL = `${BASE_URL}/primeiro-cadastro`;
const PRIMEIRO_ENVIO_URL = `${BASE_URL}/primeiro-envio`;
const RESPOSTAS_QUESTIONARIOS_URL = `${BASE_URL}/respostas`;
const TIMELINE_DATA_URL = `${BASE_URL}/timeline`;
const PLANO_ACAO_URL = `${BASE_URL}/plano-acao`;
const TENTATIVAS_ENVIO_URL = `${BASE_URL}/tentativas-envio`;

const getClienteId = () => {
  try {
    const userInfoString = localStorage.getItem('userInfo');
    if (userInfoString) {
      const userInfo = JSON.parse(userInfoString);
      return userInfo?.cliente_id;
    }
    
    const currentClienteString = localStorage.getItem('currentCliente');
    if (currentClienteString) {
      const currentCliente = JSON.parse(currentClienteString);
      return currentCliente?._id;
    }
    
    return null;
  } catch (error) {
    return null;
  }
};

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

export const FornecedoresService = {
  getStats: async (clienteId?: string): Promise<FornecedoresStats> => {
    try {
      const cliente_id = clienteId || getClienteId();
      if (!cliente_id) {
        throw new Error('ID do cliente não disponível');
      }
      
      const response = await axios.get(`${BASE_URL}/stats`, {
        params: { cliente_id },
        headers: getAuthHeader()
      });
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getPlanosAcao: async (fornecedorId?: string, clienteId?: string): Promise<PlanoAcaoStats> => {
    try {
      const cliente_id = clienteId || getClienteId();
      if (!cliente_id) {
        throw new Error('ID do cliente não disponível');
      }
      
      let params: any = { cliente_id };
      if (fornecedorId) {
        params.fornecedorId = fornecedorId;
      }
      
      const response = await axios.get(`${BASE_URL}/planos-acao`, {
        params,
        headers: getAuthHeader()
      });
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getMaturidade: async (clienteId: string, fornecedorId?: string): Promise<MaturidadeData> => {
    try {
      if (fornecedorId) {
        try {
          const questoes = await FornecedoresService.getQuestoes();
          const mapQuestoes: Record<string, any> = {};
          
          if (Array.isArray(questoes)) {
            questoes.forEach(q => {
              if (q._id) {
                let tipoQuestao = 'padrao';
                
                if (q._id === '64ee165a41736da1927a46b7' || q._id === '64ee165a41736da1927a46c7') {
                  tipoQuestao = 'multipla';
                }
                
                mapQuestoes[q._id] = {
                  ...q,
                  tipoQuestao
                };
              }
            });
          }
          
          const respostas = await FornecedoresService.getRespostasByFornecedor(fornecedorId, clienteId);
          
          if (Array.isArray(respostas) && respostas.length > 0) {
            let totalPontosPossiveis = 0;
            let pontuacaoObtida = 0;

            const questoesMaturidade = Object.values(mapQuestoes).filter(q => 
              q.tipo !== "input" && 
              q.categoria !== "identificacao" &&
              q.questao && 
              q._id
            );
            
            totalPontosPossiveis = questoesMaturidade.length;
            
            for (const questionario of respostas) {
              if (questionario.respostas && Array.isArray(questionario.respostas) && 
                  (questionario.respondido === true || questionario.status === 'respondido')) {
                
                for (const resposta of questionario.respostas) {
                  if (resposta && typeof resposta === 'object' && 'questao_id' in resposta && 
                      'resposta' in resposta) {
                      
                    const questaoId = resposta.questao_id as string;
                    const questao = mapQuestoes[questaoId];
                    
                    if (!questao || questao.tipo === "input" || questao.categoria === "identificacao") {
                      continue;
                    }
                    
                    let valorResposta = resposta.resposta;
                    
                    if (questao.tipoQuestao === 'multipla' && Array.isArray(valorResposta)) {
                      const respostasValidas = valorResposta.filter(val => 
                        val !== 'Não sei informar' && 
                        val !== 'Nenhuma Ferramenta' &&
                        val !== 'Não aplicável' &&
                        val !== 'N/A'
                      );
                      
                      if (respostasValidas.length > 0) {
                        const pontosQuestao = Math.min(respostasValidas.length * 0.5, 1.0);
                        pontuacaoObtida += pontosQuestao;
                      }
                    } 
                    else {
                      let temRespostaPositiva = false;
                      
                      if (Array.isArray(valorResposta)) {
                        temRespostaPositiva = valorResposta.some(val => 
                          val === 'Sim' || 
                          val === 'sim' || 
                          val === 'SIM' ||
                          (typeof val === 'string' && val.toLowerCase().includes('sim')) ||
                          val === true ||
                          val === 'true' ||
                          val === 1 ||
                          val === '1'
                        );
                      } else if (
                        valorResposta === 'Sim' || 
                        valorResposta === 'sim' || 
                        valorResposta === 'SIM' ||
                        (typeof valorResposta === 'string' && valorResposta.toLowerCase().includes('sim')) ||
                        valorResposta === true || 
                        valorResposta === 'true' || 
                        valorResposta === '1' || 
                        valorResposta === 1
                      ) {
                        temRespostaPositiva = true;
                      }
                      
                      if (temRespostaPositiva) {
                        pontuacaoObtida += 1;
                      }
                    }
                  }
                }
              }
            }
            
            let percentualMaturidade = 0;
            if (totalPontosPossiveis > 0) {
              percentualMaturidade = Math.round((pontuacaoObtida / totalPontosPossiveis) * 100);
            }
            
            if (percentualMaturidade === 0 && respostas.length > 0) {
              try {
                const response = await axios.get(`${BASE_URL}/maturidade`, {
                  params: { cliente_id: clienteId, fornecedor_id: fornecedorId },
                  headers: getAuthHeader()
                });
                
                if (response.data && typeof response.data.percentualMaturidade === 'number' && 
                    response.data.percentualMaturidade > 0) {
                  return {
                    percentualMaturidade: response.data.percentualMaturidade,
                    nivelConformidade: response.data.nivelConformidade || 'baixo'
                  };
                }
              } catch (apiError) {
                // Fallback silencioso
              }
            }
            
            let nivelConformidade: 'baixo' | 'medio' | 'alto' = 'baixo';
            if (percentualMaturidade >= 75) nivelConformidade = 'alto';
            else if (percentualMaturidade >= 40) nivelConformidade = 'medio';
            
            return {
              percentualMaturidade,
              nivelConformidade
            };
          } else {
            return {
              percentualMaturidade: 0,
              nivelConformidade: 'baixo' as const
            };
          }
        } catch (err) {
          // Fallback para API
        }
      }
      
      const params: any = { cliente_id: clienteId };
      if (fornecedorId) {
        params.fornecedor_id = fornecedorId;
      }
      
      const response = await axios.get(`${BASE_URL}/maturidade`, {
        params,
        headers: getAuthHeader()
      });
      
      let nivelConformidade: 'baixo' | 'medio' | 'alto' = 'baixo';
      
      if (response.data.nivelConformidade === 'alto' || 
          response.data.nivelConformidade === 'medio' || 
          response.data.nivelConformidade === 'baixo') {
        nivelConformidade = response.data.nivelConformidade;
      } else {
        const percentualMaturidade = typeof response.data.percentualMaturidade === 'number'
          ? response.data.percentualMaturidade
          : 0;
          
        if (percentualMaturidade >= 75) nivelConformidade = 'alto';
        else if (percentualMaturidade >= 40) nivelConformidade = 'medio';
      }
      
      return {
        percentualMaturidade: typeof response.data.percentualMaturidade === 'number' 
          ? response.data.percentualMaturidade
          : 0,
        nivelConformidade
      };
      
    } catch (error) {
      return {
        percentualMaturidade: 0,
        nivelConformidade: 'baixo' as const
      };
    }
  },

  calcularMaturidade: async (clienteId?: string): Promise<MaturidadeData> => {
    try {
      const cliente_id = clienteId || getClienteId();
      if (!cliente_id) {
        throw new Error('ID do cliente não disponível');
      }
      
      const [respostasResponse, questoesResponse, grauRiscosResponse] = await Promise.all([
        axios.get(`${API_URL}/analisefornecedores/${cliente_id}`, {
          headers: getAuthHeader()
        }),
        axios.get(`${API_URL}/questoes`, {
          headers: getAuthHeader()
        }),
        axios.get(`${API_URL}/grauriscos`, {
          headers: getAuthHeader()
        })
      ]);
      
      const respostas = respostasResponse.data;
      const questoes = questoesResponse.data;
      const grauRiscos = grauRiscosResponse.data;
      
      const flattenedRespostas = respostas.flatMap((item: any) => item.respostas);
      
      const mergedDataArray = flattenedRespostas.map((resposta: any) => {
        const questao = questoes.find((q: any) => q._id === resposta.questao_id);
        
        return {
          ...questao,
          respostas: resposta.resposta,
          graus: grauRiscos.filter(
            (g: any) => g.questao_id.toString() === questao?._id.toString()
          ),
        };
      });
      
      const sortedMergedData = mergedDataArray
        ?.filter((q: any) => q.tipo !== "input" && q.graus[0])
        ?.sort((a: any, b: any) => a.ordem - b.ordem);
      
      let allRespData: any = [];
      
      sortedMergedData.forEach((questao: any) => {
        if (questao.respostas && questao.respostas[0]) {
          questao.respostas.map((item: any) => {
            return allRespData.push({
              ...questao,
              resposta: item,
              grau: grauRiscos
                .filter((gr: any) => gr.questao_id.toString() === questao._id.toString())
                .filter((gr: any) => gr.value.includes(item))[0]?.grau,
            });
          });
        }
      });
      
      let meio = allRespData.filter(
        (r: any) =>
          (r.grau === "meio" || r.grau === "Ponto de Atenção") &&
          r._id !== "64ee165a41736da1927a46b7" && 
          r._id !== "64ee165a41736da1927a46c7"
      ).length / 2;
      
      const pergunta12 = allRespData.find((r: any) => r._id === "64ee165a41736da1927a46b7");
      if (pergunta12 && pergunta12.respostas) {
        meio = meio + pergunta12.respostas.filter((r: any) => r !== "Não sei informar").length / 2;
      }
      
      const pergunta28 = allRespData.find((r: any) => r._id === "64ee165a41736da1927a46c7");
      if (pergunta28 && pergunta28.respostas) {
        meio = meio + pergunta28.respostas.filter((r: any) => r !== "Nenhuma Ferramenta").length / 2;
      }
      
      const conforme = allRespData.filter((r: any) => r.grau === "sim").length;
      const somaMeioConforme = meio + conforme;
      
      const pontuacaopergunta7 = mergedDataArray.find(
        (data: any) => data?._id === "64ee165a41736da1927a46b2"
      )?.respostas?.length || 0;
      
      const percentualMaturidade = (somaMeioConforme / (21 + pontuacaopergunta7)) * 100;
      
      let nivelConformidade: 'baixo' | 'medio' | 'alto' = 'baixo';
      if (percentualMaturidade >= 75) nivelConformidade = 'alto';
      else if (percentualMaturidade >= 40) nivelConformidade = 'medio';
      
      const maturidadeCalculada: MaturidadeData = {
        percentualMaturidade: Math.round(percentualMaturidade),
        nivelConformidade
      };
      
      return maturidadeCalculada;
    } catch (error) {
      return {
        percentualMaturidade: 0,
        nivelConformidade: 'baixo' as const
      };
    }
  },

  getFornecedores: async (params: FornecedoresParams): Promise<FornecedoresPaginatedResponse> => {
    try {
      const cliente_id = params.cliente_id || getClienteId();
      if (!cliente_id) {
        throw new Error('ID do cliente não disponível');
      }
      
      const response = await axios.get(`${BASE_URL}`, { 
        params: {
          ...params,
          cliente_id
        },
        headers: getAuthHeader() 
      });
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getFornecedor: async (id: string, clienteId?: string): Promise<Fornecedor> => {
    try {
      const cliente_id = clienteId || getClienteId();
      if (!cliente_id) {
        throw new Error('ID do cliente não disponível');
      }

      const response = await axios.get(`${BASE_URL}/fornecedor/${id}`, {
        params: { cliente_id },
        headers: getAuthHeader()
      });
      
      if (response.data) {
        return response.data;
      }
      
      const listResponse = await axios.get(`${BASE_URL}`, {
        params: { 
          cliente_id,
          fornecedor_id: id
        },
        headers: getAuthHeader()
      });
      
      if (Array.isArray(listResponse.data.data)) {
        const fornecedor = listResponse.data.data.find((f: any) => f.id === id);
        if (fornecedor) {
          return fornecedor;
        }
      } 
      else if (Array.isArray(listResponse.data)) {
        const fornecedor = listResponse.data.find((f: any) => f.id === id);
        if (fornecedor) {
          return fornecedor;
        }
      }
      
      throw new Error('Fornecedor não encontrado');
    } catch (error) {
      throw error;
    }
  },

  getRespostasByFornecedor: async (fornecedorId: string, clienteId?: string): Promise<any> => {
    try {
        const cliente_id = clienteId || getClienteId();
        if (!cliente_id || !fornecedorId) {
            return [];
        }
        
        const response = await axios.get(`${API_URL}/questionarios/fornecedor/${fornecedorId}`, {
            params: { cliente_id },
            headers: getAuthHeader()
        });
        
        if (response.data && !Array.isArray(response.data)) {
            return [response.data];
        }
        
        return response.data;
    } catch (error: any) {
        if (error.response && error.response.status === 404) {
            return [];
        }
        
        throw error;
    }
  },

  getQuestoes: async (): Promise<any> => {
    try {
      const response = await axios.get(`${API_URL}/questoes`, {
        headers: getAuthHeader()
      });
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  enviarQuestionario: async (id: string): Promise<{ success: boolean; message: string }> => {
    try {
      const cliente_id = getClienteId();
      if (!cliente_id) {
        throw new Error('ID do cliente não disponível');
      }
      
      const response = await axios.post(`${BASE_URL}/${id}/enviar-questionario`, 
        { cliente_id },
        { headers: getAuthHeader() }
      );
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getRespostasQuestionarios: async (clienteId: string, fornecedorId: string): Promise<any[]> => {
    try {
      const response = await axios.get(RESPOSTAS_QUESTIONARIOS_URL, {
        params: { 
          cliente_id: clienteId,
          fornecedor_id: fornecedorId 
        },
        headers: getAuthHeader()
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      return [];
    }
  },

  getTimelineData: async (clienteId?: string, fornecedorId?: string): Promise<TimelineData> => {
    try {
      const cliente_id = clienteId || getClienteId();
      if (!cliente_id) {
        throw new Error('ID do cliente não disponível');
      }
      
      const params: any = { cliente_id };
      if (fornecedorId) {
        params.fornecedor_id = fornecedorId;
      }
      
      const response = await axios.get(TIMELINE_DATA_URL, {
        params,
        headers: getAuthHeader()
      });
      
      const data = response.data || {};
      
      const normalizedData: TimelineData = {
        inicioAcao: data.inicioAcao || null,
        cadastroFornecedor: data.cadastroFornecedor || null,
        primeiroCadastro: data.primeiroCadastro || null,
        aplicacoesQuestionarios: Array.isArray(data.aplicacoesQuestionarios) 
          ? data.aplicacoesQuestionarios 
          : [],
        disparosEmails: Array.isArray(data.disparosEmails) 
          ? data.disparosEmails 
          : [],
        respostasQuestionarios: Array.isArray(data.respostasQuestionarios) 
          ? data.respostasQuestionarios 
          : [],
        planosAplicados: Array.isArray(data.planosAplicados) 
          ? data.planosAplicados 
          : [],
        planosRespondidos: Array.isArray(data.planosRespondidos) 
          ? data.planosRespondidos 
          : [],
        liberacoesModulos: Array.isArray(data.liberacoesModulos) 
          ? data.liberacoesModulos 
          : [],
        prazos: Array.isArray(data.prazos) 
          ? data.prazos 
          : []
      };
      
      return normalizedData;
    } catch (error) {
      return {
        inicioAcao: null,
        cadastroFornecedor: null,
        aplicacoesQuestionarios: [],
        disparosEmails: [],
        respostasQuestionarios: [],
        planosAplicados: [],
        planosRespondidos: [],
        liberacoesModulos: [],
        prazos: []
      };
    }
  },

  getInicioAcao: async (clienteId: string): Promise<string | null> => {
    try {
      const response = await axios.get(INICIO_ACAO_URL, {
        params: { cliente_id: clienteId },
        headers: getAuthHeader()
      });
      return response.data.dataInicioAcao || null;
    } catch (error) {
      return null;
    }
  },

  getPrimeiroCadastroFornecedor: async (clienteId: string): Promise<string | null> => {
    try {
      const response = await axios.get(PRIMEIRO_CADASTRO_URL, {
        params: { cliente_id: clienteId },
        headers: getAuthHeader()
      });
      return response.data.dataPrimeiroCadastro || null;
    } catch (error) {
      return null;
    }
  },

  getPrimeiroEnvioQuestionario: async (clienteId: string): Promise<string | null> => {
    try {
      const response = await axios.get(PRIMEIRO_ENVIO_URL, {
        params: { cliente_id: clienteId },
        headers: getAuthHeader()
      });
      return response.data.dataPrimeiroEnvio || null;
    } catch (error) {
      return null;
    }
  },

  getDataPlanoAcao: async (clienteId: string, fornecedorId?: string): Promise<any> => {
    try {
      const cliente_id = clienteId || getClienteId();
      if (!cliente_id) {
        throw new Error('ID do cliente não disponível');
      }
      
      const params: any = { cliente_id };
      if (fornecedorId) {
        params.fornecedor_id = fornecedorId;
      }
      
      const response = await axios.get(PLANO_ACAO_URL, {
        params,
        headers: getAuthHeader()
      });
      
      return response.data;
    } catch (error) {
      return { dataPlanoAcao: null, statusAcao: null };
    }
  },

  getTentativasEnvio: async (clienteId?: string): Promise<{ data: TentativasEnvioResponse }> => {
    try {
      const cliente_id = clienteId || getClienteId();
      if (!cliente_id) {
        throw new Error('ID do cliente não disponível');
      }
      
      const response = await axios.get(TENTATIVAS_ENVIO_URL, {
        params: { cliente_id },
        headers: getAuthHeader()
      });
      
      if (response.data && (response.data.tentativas || response.data.stats)) {
        const tentativasProcessadas = (response.data.tentativas || [])
          .filter((tentativa: any) => tentativa.tentativa <= 3)
          .map((tentativa: any) => ({
            ...tentativa,
            dataInicioModulo: tentativa.dataInicioModulo || response.data.metadata?.data_inicio_modulo || null,
            dataCadastroFornecedor: tentativa.dataCadastroFornecedor || null,
            razaoSocial: tentativa.razaoSocial || 'N/A',
            email: tentativa.email || '',
            area: tentativa.area || 'N/A',
            statusResposta: tentativa.statusResposta || 'nao_respondido',
            tentativa: Math.min(tentativa.tentativa, 3) as 1 | 2 | 3
          }));
        
        let statsProcessadas = response.data.stats;
        
        if (statsProcessadas) {
          const { tentativa4, tentativa5, ...statsLimitadas } = statsProcessadas;
          
          statsProcessadas = {
            tentativa1: statsLimitadas.tentativa1 || { total: 0, respondidos: 0, naoRespondidos: 0, percentualResposta: 0 },
            tentativa2: statsLimitadas.tentativa2 || { total: 0, respondidos: 0, naoRespondidos: 0, percentualResposta: 0 },
            tentativa3: statsLimitadas.tentativa3 || { total: 0, respondidos: 0, naoRespondidos: 0, percentualResposta: 0 },
            totalGeral: statsLimitadas.totalGeral || { envios: 0, respondidos: 0, percentualResposta: 0 }
          };
          
          const novoTotal = statsProcessadas.tentativa1.total + statsProcessadas.tentativa2.total + statsProcessadas.tentativa3.total;
          const novoRespondidos = statsProcessadas.tentativa1.respondidos + statsProcessadas.tentativa2.respondidos + statsProcessadas.tentativa3.respondidos;
          
          statsProcessadas.totalGeral = {
            envios: novoTotal,
            respondidos: novoRespondidos,
            percentualResposta: novoTotal > 0 ? Math.round((novoRespondidos / novoTotal) * 100) : 0
          };
        }
        
        return { 
          data: {
            ...response.data,
            tentativas: tentativasProcessadas,
            stats: statsProcessadas,
            metadata: {
              ...response.data.metadata,
              tentativas_limitadas: true,
              max_tentativas: 3,
              observacao: "Dados limitados a máximo 3 tentativas por fornecedor"
            }
          }
        };
      }
      
      if (Array.isArray(response.data)) {
        const tentativas = response.data
          .filter((tentativa: any) => tentativa.tentativa <= 3)
          .map((tentativa: any) => ({
            ...tentativa,
            dataInicioModulo: tentativa.dataInicioModulo || null,
            dataCadastroFornecedor: tentativa.dataCadastroFornecedor || null,
            razaoSocial: tentativa.razaoSocial || 'N/A',
            email: tentativa.email || '',
            area: tentativa.area || 'N/A',
            statusResposta: tentativa.statusResposta || 'nao_respondido',
            tentativa: Math.min(tentativa.tentativa, 3) as 1 | 2 | 3
          }));
        
        const stats: TentativasEnvioStats = {
          tentativa1: { total: 0, respondidos: 0, naoRespondidos: 0, percentualResposta: 0 },
          tentativa2: { total: 0, respondidos: 0, naoRespondidos: 0, percentualResposta: 0 },
          tentativa3: { total: 0, respondidos: 0, naoRespondidos: 0, percentualResposta: 0 },
          totalGeral: { envios: tentativas.length, respondidos: 0, percentualResposta: 0 }
        };
        
        tentativas.forEach((tentativa: TentativaEnvio) => {
          const statsKey = `tentativa${tentativa.tentativa}` as 'tentativa1' | 'tentativa2' | 'tentativa3';
          if (stats[statsKey]) {
            stats[statsKey].total++;
            if (tentativa.statusResposta === 'respondido' || tentativa.statusResposta === 'em_analise') {
              stats[statsKey].respondidos++;
              stats.totalGeral.respondidos++;
            } else {
              stats[statsKey].naoRespondidos++;
            }
          }
        });
        
        (['tentativa1', 'tentativa2', 'tentativa3'] as const).forEach(key => {
          if (stats[key].total > 0) {
            stats[key].percentualResposta = Math.round((stats[key].respondidos / stats[key].total) * 100);
          }
        });
        
        if (stats.totalGeral.envios > 0) {
          stats.totalGeral.percentualResposta = Math.round((stats.totalGeral.respondidos / stats.totalGeral.envios) * 100);
        }
        
        return { data: { 
          tentativas, 
          stats,
          metadata: {
            total_fornecedores: 0,
            total_tentativas: tentativas.length,
            data_consulta: new Date().toISOString(),
            tentativas_limitadas: true,
            max_tentativas: 3
          }
        }};
      }
      
      return {
        data: {
          tentativas: [],
          stats: {
            tentativa1: { total: 0, respondidos: 0, naoRespondidos: 0, percentualResposta: 0 },
            tentativa2: { total: 0, respondidos: 0, naoRespondidos: 0, percentualResposta: 0 },
            tentativa3: { total: 0, respondidos: 0, naoRespondidos: 0, percentualResposta: 0 },
            totalGeral: { envios: 0, respondidos: 0, percentualResposta: 0 }
         },
         metadata: {
           total_fornecedores: 0,
           total_tentativas: 0,
           data_consulta: new Date().toISOString(),
           tentativas_limitadas: true,
           max_tentativas: 3,
           observacao: "Nenhum dado encontrado"
         }
       }
     };
   } catch (error) {
     return {
       data: {
         tentativas: [],
         stats: {
           tentativa1: { total: 0, respondidos: 0, naoRespondidos: 0, percentualResposta: 0 },
           tentativa2: { total: 0, respondidos: 0, naoRespondidos: 0, percentualResposta: 0 },
           tentativa3: { total: 0, respondidos: 0, naoRespondidos: 0, percentualResposta: 0 },
           totalGeral: { envios: 0, respondidos: 0, percentualResposta: 0 }
         },
         metadata: {
           total_fornecedores: 0,
           total_tentativas: 0,
           data_consulta: new Date().toISOString(),
           tentativas_limitadas: true,
           max_tentativas: 3,
           erro: true,
           observacao: "Erro ao carregar dados"
         }
       }
     };
   }
 },
};

export default FornecedoresService;